import styled from 'styled-components';

import EditIconSvg from '../../../assets/icons/icon-my-page-edit.svg?react';
import profileAvatarDefaultImage from '../../../assets/images/profile-avatar-default.png';
import { resolveRenderableAvatarUrl } from '../../../shared/model';
import { BaseButton, ProfileRating, StatusToggle } from '../../../shared/ui';

type MyPageProfileEditTopRowProps = {
  avatarUrl?: string | null;
  avatarEditAriaLabel: string;
  isRatingVisible: boolean;
  onAvatarEditPress: () => void;
  onRatingVisibilityChange: (isVisible: boolean) => void;
  ratingLabel: string;
  ratingValueLabel: string;
};

const EditRow = styled.div.attrs({
  'data-ui': 'my-page-profile-edit-top-row',
})`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.scale.px(12)};
`;
const AvatarFrame = styled.div`
  width: ${({ theme }) => theme.scale.px(89)};
  height: ${({ theme }) => theme.scale.px(90)};
  position: relative;
`;
const Avatar = styled.img.attrs({
  alt: '',
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(85)};
  height: ${({ theme }) => theme.scale.px(85)};
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;
const EditBadge = styled(BaseButton).attrs({ type: 'button' })`
  width: ${({ theme }) => theme.scale.px(27)};
  height: ${({ theme }) => theme.scale.px(27)};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface.subtle};
  position: absolute;
  right: 0;
  bottom: 0;
  display: grid;
  place-items: center;
`;
const EditIcon = styled(EditIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(18)};
  height: ${({ theme }) => theme.scale.px(18)};
  & path {
    fill: ${({ theme }) => theme.colors.text.primary};
  }
`;
const RatingContainer = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(7)};
  padding-top: ${({ theme }) => theme.scale.px(12)};
`;
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(7)};
`;
const ToggleLabel = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(15)};
`;

export function MyPageProfileEditTopRow({
  avatarUrl = null,
  avatarEditAriaLabel,
  isRatingVisible,
  onAvatarEditPress,
  onRatingVisibilityChange,
  ratingLabel,
  ratingValueLabel,
}: MyPageProfileEditTopRowProps) {
  const avatarSource =
    resolveRenderableAvatarUrl(avatarUrl) ?? profileAvatarDefaultImage;

  return (
    <EditRow>
      <AvatarFrame>
        <Avatar src={avatarSource} />
        <EditBadge aria-label={avatarEditAriaLabel} onClick={onAvatarEditPress}>
          <EditIcon />
        </EditBadge>
      </AvatarFrame>
      <RatingContainer>
        <ToggleRow>
          <ToggleLabel>{ratingLabel}</ToggleLabel>
          <StatusToggle
            ariaLabel={ratingLabel}
            checked={isRatingVisible}
            onToggle={onRatingVisibilityChange}
          />
        </ToggleRow>
        {isRatingVisible ? (
          <ProfileRating
            dataUi="my-page-profile-rating-stars"
            starSizePx={18}
            valueFontSizePx={21}
            valueLabel={ratingValueLabel}
            valueTone="subdued"
          />
        ) : null}
      </RatingContainer>
    </EditRow>
  );
}
